import { styled, css, type YakTheme } from 'next-yak'
import {
  type PolymorphicComponent,
  Polymorphic,
  type YakComponentOf,
  withPrivatePublicMap,
} from '@owl-nest/yak/extra-runtime'

import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as bp from '../constants/breakpoints'
import { Icon } from '../icons/glyphs'

export type BaseLinkProps = {
  /** @default false */
  disabled?: boolean
  /** @default false */
  underlined?: boolean
  /** @default false */
  uppercase?: boolean
  /** @default false */
  withIcon?: boolean
}

type PrivateBaseLinkProps = { [K in keyof BaseLinkProps as `$${K}`]: BaseLinkProps[K] }

export const _baseStyle = css`
  cursor: pointer;
  font-family: ${fonts.PRIMARY_FONT};
  font-size: inherit;
  font-weight: 600;
  outline: none;
  text-decoration: none;
  transition: color 200ms ease-in-out;
`

const PrivateBase = styled(Polymorphic.a)<PrivateBaseLinkProps>`
  ${_baseStyle};

  ${({ $disabled = false }) =>
    $disabled &&
    css`
      &[href],
      button& {
        pointer-events: none;
        color: ${colors.BACKGROUND_BUTTON_DISABLED};
      }

      color: ${colors.BACKGROUND_BUTTON_DISABLED};
    `}

  ${({ $underlined = false }) =>
    $underlined &&
    css`
      text-decoration: underline;
    `}

  ${({ $uppercase = false }) =>
    $uppercase &&
    css`
      text-transform: uppercase;
    `}

  ${({ $withIcon = false }) =>
    $withIcon &&
    css`
      align-items: center;
      display: flex;

      ${Icon} {
        color: ${colors.PRIMARY_GREY_500};
        margin-right: 5px;
      }

      ${Icon} + * {
        vertical-align: middle;
      }
    `}
` as YakComponentOf<PolymorphicComponent<'a', PrivateBaseLinkProps>>

export const Base = styled(
  withPrivatePublicMap(PrivateBase, ({ disabled, underlined, uppercase, withIcon, ...props }: BaseLinkProps) => ({
    ...props,
    $disabled: disabled,
    $underlined: underlined,
    $uppercase: uppercase,
    $withIcon: withIcon,
  })),
)`` as YakComponentOf<PolymorphicComponent<'a', BaseLinkProps>>

const _primaryTintedColor = css`
  color: ${({ theme }) => getPrimaryTintedColor(theme)};
`

export function getPrimaryTintedColor(theme: YakTheme) {
  return (theme as any)?.colors?.primary ?? colors.PRIMARY_BLUE_500
}

type VariantProps = {
  darkened?: boolean
  tinted?: boolean
}

type PrivateVariantProps = { [K in keyof VariantProps as `$${K}`]: VariantProps[K] }

export const _primaryStyle = css`
  color: ${colors.PRIMARY_GREY_900};

  @media screen and ${bp.LAPTOP} {
    &:active,
    &:hover {
      &[href],
      button& {
        color: ${colors.PRIMARY_BLUE_700};
      }
    }
  }
`

const PrivatePrimary = styled(Base)<PrivateVariantProps>`
  ${_primaryStyle};

  ${({ $darkened = false, $tinted = false }) => {
    if ($tinted && $darkened) {
      return css`
        color: ${({ theme }) => `${(theme as any)?.colors?.hover ?? colors.PRIMARY_BLUE_700}`};
      `
    } else if ($tinted) {
      return _primaryTintedColor
    }
  }};

  @media screen and ${bp.LAPTOP} {
    ${({ $darkened = false, theme, $tinted = false }) => {
      let activeColor = (theme as any)?.colors?.hover as string | undefined
      if ($tinted && $darkened) {
        activeColor = ((theme as any)?.colors?.hover as string | undefined) ?? colors.PRIMARY_BLUE_900
      }

      return (
        activeColor &&
        css`
          &:active,
          &:hover {
            &[href],
            button& {
              color: ${() => activeColor};
            }
          }
        `
      )
    }};
  }
`

export type PrimaryProps = BaseLinkProps & VariantProps

export const Primary = styled(
  withPrivatePublicMap(PrivatePrimary, ({ darkened, tinted, ...props }: PrimaryProps) => ({
    ...props,
    $darkened: darkened,
    $tinted: tinted,
  })),
)`` as YakComponentOf<PolymorphicComponent<'a', PrimaryProps>>

Primary.displayName = 'S.link.Primary'

export const _secondaryStyle = css`
  color: ${colors.TEXT_TERTIARY};
  font-weight: 400;
  font-size: 14px;

  &:active,
  &:hover {
    &[href],
    button& {
      text-decoration: underline;
    }
  }
`

export const Secondary = styled(Base)<VariantProps>`
  ${_secondaryStyle};
` as YakComponentOf<PolymorphicComponent<'a', BaseLinkProps & VariantProps>>
Secondary.displayName = 'S.link.Secondary'

export const Inverted = styled(Base)<VariantProps>`
  color: ${colors.PRIMARY_GREY_000};

  &:hover,
  &:active {
    &[href],
    button& {
      text-decoration: underline;
    }
  }
` as YakComponentOf<PolymorphicComponent<'a', BaseLinkProps & VariantProps>>
Inverted.displayName = 'S.link.Inverted'

export const Important = styled(Base)<VariantProps>`
  color: ${colors.PRIMARY_RED_500};

  &:hover,
  &:active {
    &[href],
    button& {
      color: ${colors.PRIMARY_RED_700};
      text-decoration: underline;
    }
  }
` as YakComponentOf<PolymorphicComponent<'a', BaseLinkProps & VariantProps>>
Important.displayName = 'S.link.Important'
